/**
 * SmartPlAIte Design Token System v2
 * 
 * Compass-born design language. Every visual decision traces back
 * to the Feature Compass: ring geometry, tier colors, radial patterns.
 * 
 * Token categories:
 *   colors    → All color values (backgrounds, text, accents, tiers)
 *   gradients → Gradient definitions
 *   typography → Font sizes, weights, line heights
 *   radius    → Border radius scale
 *   shadows   → Box shadow definitions
 *   spacing   → 4px-based spacing scale
 *   motion    → Transition/animation tokens
 *   
 * Usage:
 *   import { brand } from '../styles/brand';
 *   const { colors, gradients } = brand;
 */

// ═══════════════════════════════════════════════════════════════════════════════
// COLORS — Derived from compass ring palette
// ═══════════════════════════════════════════════════════════════════════════════
export const colors = {
  // Backgrounds (void → surface → elevated)
  bg0: '#060610',           // The void — deepest layer
  bg1: '#0A0A18',           // Primary surface
  bg2: '#0F0F22',           // Elevated surface (cards)
  bg3: '#16162E',           // Highest elevation (modals, popovers)
  
  // Panels & Cards — glass layers
  panel: 'rgba(255, 255, 255, 0.03)',
  panelHover: 'rgba(255, 255, 255, 0.06)',
  panel2: 'rgba(255, 255, 255, 0.05)',
  glass: 'rgba(255, 255, 255, 0.05)',
  glassStrong: 'rgba(255, 255, 255, 0.08)',
  
  // Borders
  stroke: 'rgba(255, 255, 255, 0.08)',
  strokeLight: 'rgba(255, 255, 255, 0.10)',
  strokeActive: 'rgba(255, 255, 255, 0.16)',
  
  // Text hierarchy
  text: '#FFFFFF',
  textSecondary: '#A1A1AA',
  mutedText: '#52525B',
  textDisabled: 'rgba(255, 255, 255, 0.15)',
  
  // Compass tier colors (the DNA)
  free: '#00E5A0',
  freeDim: 'rgba(0, 229, 160, 0.10)',
  freeBorder: 'rgba(0, 229, 160, 0.20)',
  
  smart: '#3B82F6',
  smartDim: 'rgba(59, 130, 246, 0.10)',
  smartBorder: 'rgba(59, 130, 246, 0.20)',
  
  genius: '#A855F7',
  geniusDim: 'rgba(168, 85, 247, 0.10)',
  geniusBorder: 'rgba(168, 85, 247, 0.20)',
  
  // Accent (derived from genius purple → blue blend)
  accent: '#7C5CFC',
  accentDim: 'rgba(124, 92, 252, 0.12)',
  accentBorder: 'rgba(124, 92, 252, 0.25)',
  accentGlow: 'rgba(124, 92, 252, 0.35)',
  
  // Functional (consistent with tier language)
  success: '#00E5A0',       // Same as free tier — green = good
  warning: '#F59E0B',       // Amber
  error: '#EF4444',         // Red
  info: '#3B82F6',          // Same as smart tier
  
  // Macro colors (distinctive, data-optimized)
  protein: '#F43F5E',       // Rose — bold, vital
  carbs: '#EAB308',         // Gold — energy
  fat: '#8B5CF6',           // Violet — rich
  fiber: '#A855F7',         // Matches genius
};

// ═══════════════════════════════════════════════════════════════════════════════
// GRADIENTS — Radial patterns inspired by compass geometry
// ═══════════════════════════════════════════════════════════════════════════════
export const gradients = {
  // Primary CTA — purple → blue arc
  primary: 'linear-gradient(135deg, #7C5CFC 0%, #3B82F6 100%)',
  
  // Background — subtle radial glow from center (compass spotlight)
  background: `radial-gradient(ellipse 80% 50% at 50% 30%, rgba(124, 92, 252, 0.06) 0%, transparent 60%), 
               linear-gradient(180deg, #060610 0%, #0A0A18 50%, #0F0F22 100%)`,
  
  // Compass ring glow
  compassGlow: 'conic-gradient(from 0deg, rgba(0,229,160,0.15), rgba(59,130,246,0.15), rgba(168,85,247,0.15), rgba(124,92,252,0.15), rgba(0,229,160,0.15))',
  
  // Card shimmer
  cardHighlight: 'linear-gradient(180deg, rgba(255,255,255,0.03) 0%, transparent 60%)',
  
  // Text accent gradient
  textAccent: 'linear-gradient(135deg, #7C5CFC 0%, #A855F7 50%, #3B82F6 100%)',
  
  // Tier gradients
  freeGrad: 'linear-gradient(135deg, #00E5A0 0%, #00C48C 100%)',
  smartGrad: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
  geniusGrad: 'linear-gradient(135deg, #A855F7 0%, #7C5CFC 100%)',
  
  // Calorie ring gradient
  calorieRing: 'conic-gradient(from -90deg, #7C5CFC, #3B82F6, #00E5A0)',
  
  // Glass overlay
  glassOverlay: 'linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.01) 100%)',
};

// ═══════════════════════════════════════════════════════════════════════════════
// TYPOGRAPHY — Clean, systematic scale
// ═══════════════════════════════════════════════════════════════════════════════
export const typography = {
  fontFamily: "'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  headingFont: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
  monoFont: "'JetBrains Mono', 'SF Mono', 'Fira Code', monospace",
  
  // Size scale (strict — no orphan sizes)
  xs: { fontSize: '10px', lineHeight: '1.4' },
  sm: { fontSize: '12px', lineHeight: '1.4' },
  base: { fontSize: '14px', lineHeight: '1.5' },
  lg: { fontSize: '16px', lineHeight: '1.5' },
  xl: { fontSize: '20px', lineHeight: '1.3' },
  '2xl': { fontSize: '24px', lineHeight: '1.25' },
  '3xl': { fontSize: '32px', lineHeight: '1.2' },
  '4xl': { fontSize: '40px', lineHeight: '1.1' },
  
  // Semantic aliases (backwards compat)
  h1: { fontSize: '28px', fontWeight: '700', lineHeight: '1.2', letterSpacing: '-0.02em' },
  h2: { fontSize: '20px', fontWeight: '600', lineHeight: '1.3', letterSpacing: '-0.01em' },
  h3: { fontSize: '16px', fontWeight: '600', lineHeight: '1.35' },
  body: { fontSize: '14px', fontWeight: '400', lineHeight: '1.5' },
  bodySmall: { fontSize: '12px', fontWeight: '400', lineHeight: '1.5' },
  caption: { fontSize: '10px', fontWeight: '600', lineHeight: '1.4', letterSpacing: '0.06em', textTransform: 'uppercase' },
  button: { fontSize: '14px', fontWeight: '600', lineHeight: '1' },
  buttonSmall: { fontSize: '12px', fontWeight: '600', lineHeight: '1' },
  
  // Weight tokens
  weights: { normal: '400', medium: '500', semibold: '600', bold: '700', heavy: '800' },
};

// ═══════════════════════════════════════════════════════════════════════════════
// RADIUS — Consistent scale
// ═══════════════════════════════════════════════════════════════════════════════
export const radius = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
  '2xl': '24px',
  pill: '100px',
  round: '50%',
};

// ═══════════════════════════════════════════════════════════════════════════════
// SHADOWS — Depth system
// ═══════════════════════════════════════════════════════════════════════════════
export const shadows = {
  sm: '0 2px 8px rgba(0, 0, 0, 0.2)',
  md: '0 4px 16px rgba(0, 0, 0, 0.25)',
  lg: '0 8px 32px rgba(0, 0, 0, 0.3)',
  xl: '0 16px 48px rgba(0, 0, 0, 0.4)',
  glow: '0 0 20px rgba(124, 92, 252, 0.25)',
  glowStrong: '0 0 40px rgba(124, 92, 252, 0.35)',
  glowFree: '0 0 20px rgba(0, 229, 160, 0.2)',
  glowSmart: '0 0 20px rgba(59, 130, 246, 0.2)',
  glowGenius: '0 0 20px rgba(168, 85, 247, 0.2)',
  inset: 'inset 0 1px 0 rgba(255, 255, 255, 0.06)',
  // backwards compat
  soft: '0 4px 12px rgba(0, 0, 0, 0.2)',
  medium: '0 8px 24px rgba(0, 0, 0, 0.25)',
};

// ═══════════════════════════════════════════════════════════════════════════════
// SPACING — 4px base unit
// ═══════════════════════════════════════════════════════════════════════════════
export const spacing = {
  s1: '4px',
  s2: '8px',
  s3: '12px',
  s4: '16px',
  s5: '20px',
  s6: '24px',
  s7: '32px',
  s8: '40px',
  s9: '48px',
  s10: '64px',
};

// ═══════════════════════════════════════════════════════════════════════════════
// MOTION — Transition tokens
// ═══════════════════════════════════════════════════════════════════════════════
export const motion = {
  fast: '0.1s ease',
  normal: '0.2s ease',
  slow: '0.35s ease',
  spring: '0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
  
  // CSS transition shorthand helpers
  hover: 'opacity 0.15s ease, transform 0.15s ease, background 0.15s ease',
  expand: 'height 0.3s ease, opacity 0.25s ease',
  fadeIn: 'opacity 0.25s ease',
};

// ═══════════════════════════════════════════════════════════════════════════════
// SAFE AREA (iOS Safari)
// ═══════════════════════════════════════════════════════════════════════════════
export const safeArea = {
  paddingTop: 'env(safe-area-inset-top, 0px)',
  paddingBottom: 'env(safe-area-inset-bottom, 20px)',
  paddingLeft: 'env(safe-area-inset-left, 0px)',
  paddingRight: 'env(safe-area-inset-right, 0px)',
};

// ═══════════════════════════════════════════════════════════════════════════════
// STYLE HELPERS — Use tokens, never hardcode
// ═══════════════════════════════════════════════════════════════════════════════

export const getPrimaryButtonStyle = ({ fullWidth = true } = {}) => ({
  width: fullWidth ? '100%' : 'auto',
  padding: '14px 24px',
  background: gradients.primary,
  border: 'none',
  borderRadius: radius.md,
  color: colors.text,
  fontFamily: typography.fontFamily,
  fontSize: typography.button.fontSize,
  fontWeight: typography.button.fontWeight,
  cursor: 'pointer',
  transition: motion.hover,
  boxShadow: shadows.glow,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: spacing.s3,
  minHeight: '48px',
  WebkitTapHighlightColor: 'transparent',
});

export const getSecondaryButtonStyle = ({ fullWidth = false } = {}) => ({
  width: fullWidth ? '100%' : 'auto',
  flex: fullWidth ? undefined : 1,
  padding: '12px 20px',
  background: colors.glass,
  border: `1px solid ${colors.stroke}`,
  borderRadius: radius.md,
  color: colors.textSecondary,
  fontFamily: typography.fontFamily,
  fontSize: typography.buttonSmall.fontSize,
  fontWeight: typography.buttonSmall.fontWeight,
  cursor: 'pointer',
  transition: motion.hover,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: spacing.s2,
  minHeight: '44px',
  WebkitTapHighlightColor: 'transparent',
});

export const getCardStyle = ({ elevated = false, tier = null } = {}) => ({
  background: elevated ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.03)',
  border: `1px solid ${tier ? colors[`${tier}Border`] || 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.08)'}`,
  borderRadius: radius.xl,
  padding: spacing.s6,
  backdropFilter: 'blur(16px)',
  WebkitBackdropFilter: 'blur(16px)',
  boxShadow: tier ? `0 0 30px ${colors[`${tier}Dim`] || 'transparent'}` : '0 4px 24px rgba(0,0,0,0.2)',
});

export const getGradientTextStyle = () => ({
  background: gradients.textAccent,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
});

// ═══════════════════════════════════════════════════════════════════════════════
// COMBINED EXPORT
// ═══════════════════════════════════════════════════════════════════════════════
export const brand = {
  colors,
  gradients,
  typography,
  radius,
  shadows,
  spacing,
  motion,
  safeArea,
};

export default brand;
